import React, { useState, useRef, useEffect } from "react";
import styles from "./InputRange.module.scss";

interface IInputRangeProps {
  min: number;
  max: number;
  step?: number;
  label: string;
  onRangeChange: (range: [number, number]) => void;
}

const InputRange: React.FC<IInputRangeProps> = ({
  min,
  max,
  step = 1,
  label,
  onRangeChange,
}) => {
  const [range, setRange] = useState<[number, number]>([min, max]);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onRangeChange(range);
  }, [range, onRangeChange]);

  const handleMouseMove = (index: number, event: MouseEvent | TouchEvent) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX =
      event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const newValue = Math.min(
      max,
      Math.max(min, min + ((clientX - rect.left) / rect.width) * (max - min)),
    );
    setRange((prev) => {
      const newRange = [...prev] as [number, number];
      newRange[index] = Math.round(newValue / step) * step;
      if (newRange[0] >= newRange[1]) {
        newRange[index] = prev[index];
      }
      return newRange;
    });
  };

  const handleMouseDown = (index: number) => {
    const handleMouseMoveBound = handleMouseMove.bind(null, index);
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMoveBound);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleMouseMoveBound);
      document.removeEventListener("touchend", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMoveBound);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleMouseMoveBound);
    document.addEventListener("touchend", handleMouseUp);
  };

  const handleTouchStart = (index: number) => {
    const handleMouseMoveBound = handleMouseMove.bind(null, index);
    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleMouseMoveBound);
      document.removeEventListener("touchend", handleTouchEnd);
    };
    document.addEventListener("touchmove", handleMouseMoveBound);
    document.addEventListener("touchend", handleTouchEnd);
  };

  return (
    <div className={styles.rangeContainer}>
      <label className={styles.label}>
        {label}{" "}
        <span className={styles.value}>
          {range[0]} – {range[1]}
        </span>
      </label>
      <div className={styles.container}>
        <p className={styles.size}>{min}</p>
        <div className={styles.sliderWrapper} ref={sliderRef}>
          <div className={styles.track}></div>
          <div
            className={styles.filledTrack}
            style={{
              left: `${((range[0] - min) / (max - min)) * 100}%`,
              right: `${100 - ((range[1] - min) / (max - min)) * 100}%`,
            }}
          ></div>
          <div
            className={`${styles.thumb} ${styles.thumbLeft}`}
            style={{
              left: `${((range[0] - min) / (max - min)) * 100}%`,
            }}
            onMouseDown={() => handleMouseDown(0)}
            onTouchStart={() => handleTouchStart(0)}
          ></div>
          <div
            className={`${styles.thumb} ${styles.thumbRight}`}
            style={{
              left: `${((range[1] - min) / (max - min)) * 100}%`,
            }}
            onMouseDown={() => handleMouseDown(1)}
            onTouchStart={() => handleTouchStart(1)}
          ></div>
        </div>
        <p className={styles.size}>{max}</p>
      </div>
    </div>
  );
};

export default InputRange;
